import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { eventApi, tournamentApi, profileApi } from '../../../services/api';
import { sportsApi } from '../../../services/sports.api';
import ImportEntriesModal from '../modals/ImportEntriesModal';
import toast from 'react-hot-toast';

const DRAW_TYPE_LABELS = { knockout: 'Knockout', league: 'League', knockout_with_playoff: 'Knockout + Playoff' };
const DUMMY_SIZES = [8, 16, 32, 64, 128, 256];

// Fallbacks used only when the sport config hasn't loaded yet
const FALLBACK_EVENT_TYPES      = ['Singles', 'Doubles', 'Mixed Doubles'];
const FALLBACK_EVENT_CATEGORIES = ['Men', 'Women', 'Boys', 'Girls', 'Mixed'];
const FALLBACK_AGE_CATEGORIES   = ['Open', 'Senior'];
const FALLBACK_DRAW_TYPES       = ['knockout', 'league', 'knockout_with_playoff'];

// Filter age categories based on event category:
//   Boys / Girls  → juniors only  (U9, U11, …, U19)
//   Men / Women   → adult only    (Senior, 35+, 40+, …)
//   Mixed / other → all
function getFilteredAgeCategories(allAgeCategories, eventCategory) {
  const cat = (eventCategory || '').replace(/['\s]/g, '').toLowerCase();
  if (cat === 'boys' || cat === 'girls') {
    return allAgeCategories.filter(a => /^U\d+$/i.test(a));
  }
  if (cat === 'men' || cat === 'women') {
    return allAgeCategories.filter(a => a === 'Senior' || a === 'Open' || /^\d+\+$/.test(a));
  }
  return allAgeCategories; // Mixed → all
}

// ─── Standard event naming convention ────────────────────────────────────────
// Maps (eventType, eventCategory) → 2-letter code: MS, WD, XD, U19 BS, etc.
const _CAT = { Men: 'M', "Men's": 'M', Women: 'W', "Women's": 'W', Boys: 'B', "Boys'": 'B', Girls: 'G', "Girls'": 'G', Mixed: 'X' };
const _TYPE = { Singles: 'S', Doubles: 'D', 'Mixed Doubles': 'D' };

function buildEventCode(eventType, eventCategory) {
  if (eventType === 'Mixed Doubles') return 'XD';
  return (_CAT[eventCategory] || '') + (_TYPE[eventType] || '');
}

function buildStandardEventName(eventType, eventCategory, ageCategory) {
  const code = buildEventCode(eventType, eventCategory);
  if (!code) return '';
  if (/^\d+\+$/.test(ageCategory))  return `${code} ${ageCategory}`;  // Masters: "35+" → "MS 35+"
  if (/^U\d+$/i.test(ageCategory))  return `${ageCategory} ${code}`;  // Junior:  "U19" → "U19 BS"
  return code;                                                          // Senior/Open → bare code
}

const STATUS_STYLE = {
  registration: { bg: '#dbeafe', color: '#1d4ed8', label: 'Registration' },
  seeding:      { bg: '#fef3c7', color: '#b45309', label: 'Seeding' },
  in_progress:  { bg: '#d1fae5', color: '#065f46', label: 'In Progress' },
  completed:    { bg: '#f3f4f6', color: '#6b7280', label: 'Completed' },
};

const Badge = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {s.label}
    </span>
  );
};

// ─── Add Event Form ───────────────────────────────────────────────────────────

const AddEventForm = ({ tournament, sportType, onSaved, onCancel }) => {
  const [sportConfig, setSportConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    sportsApi.getConfig(sportType)
      .then(res => { if (!cancelled) setSportConfig(res.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setConfigLoading(false); });
    return () => { cancelled = true; };
  }, [sportType]);

  const eventTypes      = sportConfig?.eventTypes      || FALLBACK_EVENT_TYPES;
  const eventCategories = sportConfig?.eventCategories || FALLBACK_EVENT_CATEGORIES;
  const ageCategories   = sportConfig?.ageCategories   || FALLBACK_AGE_CATEGORIES;
  const drawTypes       = sportConfig?.drawTypes       || FALLBACK_DRAW_TYPES;
  const defaultMax      = sportConfig?.defaults?.maxParticipants || 32;

  const [form, setForm]   = useState(null);   // current row being configured
  const [queue, setQueue] = useState([]);     // events pending creation
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const EVENT_STAGES = ['General', 'Main Draw', 'Qualifying'];

  const mainDrawMax = (eventType) => {
    const isDoubles = eventType && eventType.toLowerCase().includes('doubles');
    return isDoubles ? 32 : 64;
  };

  // Initialise form once config is ready
  useEffect(() => {
    if (!configLoading && !form) {
      const et = eventTypes[0] || 'Singles';
      const ec = eventCategories[0] || 'Men';
      const ac = ageCategories[0] || 'Open';
      setForm({
        event_type: et, event_category: ec, age_category: ac,
        draw_type: drawTypes[0] || 'knockout',
        max_participants: defaultMax,
        entry_fee: tournament?.entry_fee ?? 0,
        event_stage: 'General',
        name: buildStandardEventName(et, ec, ac),
        has_qualifying: false,
        qualifying_spots: null,
        qualifying_draw_size: null,
      });
    }
  }, [configLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update a field; auto-regenerate name when type/category/age changes;
  // auto-set max_participants when event_stage becomes Main Draw
  const set = (k, v) => {
    setForm(prev => {
      const next = { ...prev, [k]: v };
      if (['event_type', 'event_category', 'age_category'].includes(k)) {
        next.name = buildStandardEventName(next.event_type, next.event_category, next.age_category);
      }
      // When event_category changes, reset age_category to first valid option for the new category
      if (k === 'event_category') {
        const filtered = getFilteredAgeCategories(ageCategories, v);
        if (filtered.length > 0 && !filtered.includes(prev.age_category)) {
          next.age_category = filtered[0];
          next.name = buildStandardEventName(next.event_type, v, filtered[0]);
        }
      }
      if (k === 'event_stage' && v === 'Main Draw') {
        next.max_participants = mainDrawMax(next.event_type);
      }
      if (k === 'event_type' && next.event_stage === 'Main Draw') {
        next.max_participants = mainDrawMax(v);
      }
      // Clear qualifying settings if stage is not Main Draw
      if (k === 'event_stage' && v !== 'Main Draw') {
        next.has_qualifying = false;
        next.qualifying_spots = null;
        next.qualifying_draw_size = null;
      }
      // When toggling off qualifying, clear child fields
      if (k === 'has_qualifying' && !v) {
        next.qualifying_spots = null;
        next.qualifying_draw_size = null;
      }
      return next;
    });
  };

  const handleAddToQueue = () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setQueue(prev => [...prev, { ...form }]);
    // Reset to defaults but keep draw/max/fee settings
    const et = eventTypes[0] || 'Singles';
    const ec = eventCategories[0] || 'Men';
    const ac = ageCategories[0] || 'Open';
    setForm(prev => ({
      event_type: et, event_category: ec, age_category: ac,
      draw_type: prev.draw_type,
      max_participants: prev.max_participants,
      entry_fee: prev.entry_fee,
      event_stage: prev.event_stage,
      name: buildStandardEventName(et, ec, ac),
      has_qualifying: false,
      qualifying_spots: null,
      qualifying_draw_size: null,
    }));
    setError('');
  };

  const handleCreate = async () => {
    const toCreate = [...queue, ...(form?.name.trim() ? [{ ...form }] : [])];
    if (toCreate.length === 0) { setError('Configure at least one event'); return; }
    setSaving(true);
    setError('');
    try {
      for (const ev of toCreate) {
        await eventApi.create(tournament.id, ev);
      }
      setQueue([]);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    } finally { setSaving(false); }
  };

  if (configLoading || !form) {
    return (
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
        Loading sport configuration...
      </div>
    );
  }

  const totalToCreate = queue.length + (form.name.trim() ? 1 : 0);

  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', marginBottom: '1rem' }}>Add Events</div>
      {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <div className="grid grid-2" style={{ marginBottom: '0.75rem' }}>
        <div className="form-group">
          <label className="form-label">Event Type</label>
          <select className="form-select" value={form.event_type} onChange={e => set('event_type', e.target.value)}>
            {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Category</label>
          <select className="form-select" value={form.event_category} onChange={e => set('event_category', e.target.value)}>
            {eventCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '0.75rem' }}>
        <div className="form-group">
          <label className="form-label">Age Category</label>
          <select className="form-select" value={form.age_category} onChange={e => set('age_category', e.target.value)}>
            {getFilteredAgeCategories(ageCategories, form.event_category).map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Draw Type</label>
          <select className="form-select" value={form.draw_type} onChange={e => set('draw_type', e.target.value)}>
            {drawTypes.map(dt => <option key={dt} value={dt}>{DRAW_TYPE_LABELS[dt] || dt}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '0.75rem' }}>
        <div className="form-group">
          <label className="form-label">Event Stage</label>
          <select className="form-select" value={form.event_stage} onChange={e => set('event_stage', e.target.value)}>
            {EVENT_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Max Entries</label>
          <input type="number" className="form-input" value={form.max_participants} min={2} max={256}
            onChange={e => set('max_participants', parseInt(e.target.value) || defaultMax)} />
          {form.event_stage === 'Main Draw' && (
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 3 }}>
              Auto-set for Main Draw ({form.event_type?.toLowerCase().includes('doubles') ? '32 doubles' : '64 singles'})
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '0.75rem' }}>
        <div className="form-group">
          <label className="form-label">Entry Fee</label>
          <input type="number" className="form-input" value={form.entry_fee} min={0} step={1}
            onChange={e => set('entry_fee', parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      {/* Qualifying settings — only for Main Draw events */}
      {form.event_stage === 'Main Draw' && (
        <div style={{ background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#115E59', marginBottom: form.has_qualifying ? '0.75rem' : 0 }}>
            <input
              type="checkbox"
              checked={!!form.has_qualifying}
              onChange={e => set('has_qualifying', e.target.checked)}
            />
            Has Qualifying Round
          </label>
          {form.has_qualifying && (
            <div className="grid grid-2" style={{ marginTop: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Qualifying Draw Size</label>
                <select className="form-select" value={form.qualifying_draw_size || ''}
                  onChange={e => set('qualifying_draw_size', parseInt(e.target.value) || null)}>
                  <option value="">Select…</option>
                  {[8, 16, 32, 64, 128, 256].map(n => (
                    <option key={n} value={n}>{n} players</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Q Slots in Main Draw</label>
                <select className="form-select" value={form.qualifying_spots || ''}
                  onChange={e => set('qualifying_spots', parseInt(e.target.value) || null)}>
                  <option value="">Select…</option>
                  {[1, 2, 4, 8, 16].map(n => (
                    <option key={n} value={n}>{n} spot{n !== 1 ? 's' : ''}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>Placeholder Q slots reserved in the main draw</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="form-group" style={{ marginBottom: '0.75rem' }}>
        <label className="form-label">Event Name</label>
        <input type="text" className="form-input" value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. MS 35+" />
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 3 }}>Auto-generated from selections — you can override</div>
      </div>

      {/* Queue list */}
      {queue.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: '0.75rem' }}>
          <div style={{ background: '#f3f4f6', padding: '0.35rem 0.75rem', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Queued ({queue.length})
          </div>
          {queue.map((ev, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', padding: '0.4rem 0.75rem', borderTop: '1px solid #f3f4f6', gap: '0.5rem', fontSize: '0.82rem' }}>
              <code style={{ fontWeight: 700, color: '#374151', flex: 1 }}>{ev.name}</code>
              <span style={{ color: '#9ca3af', fontSize: '0.72rem' }}>{DRAW_TYPE_LABELS[ev.draw_type] || ev.draw_type}</span>
              {ev.entry_fee > 0 && <span style={{ color: '#059669', fontSize: '0.72rem' }}>₹{ev.entry_fee}</span>}
              <button onClick={() => setQueue(prev => prev.filter((_, i) => i !== idx))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.85rem', padding: '0 2px', lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
        {queue.length > 0 && (
          <span style={{ fontSize: '0.75rem', color: '#6b7280', marginRight: 'auto' }}>
            {totalToCreate} event{totalToCreate !== 1 ? 's' : ''} to create
          </span>
        )}
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
        {form.name.trim() && (
          <button type="button" className="btn btn-outline btn-sm" onClick={handleAddToQueue}>+ Queue</button>
        )}
        <button type="button" className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving || totalToCreate === 0}>
          {saving ? 'Creating…' : `Create${totalToCreate > 1 ? ` All ${totalToCreate}` : ''}`}
        </button>
      </div>
    </div>
  );
};

// ─── BWF Seed Rules ───────────────────────────────────────────────────────────

const getBWFSeedInfo = (entryCount) => {
  if (entryCount < 16) return { max: 2,  label: '<16 entries → max 2 seeds' };
  if (entryCount < 32) return { max: 4,  label: '16–31 entries → max 4 seeds' };
  if (entryCount < 64) return { max: 8,  label: '32–63 entries → max 8 seeds' };
  return               { max: 16, label: '64+ entries → max 16 seeds' };
};

// ─── Player Search Input (autocomplete against player profiles) ───────────────
// Renders a text input that debounce-searches the global player profile store.
// When a user picks a suggestion the parent receives the full profile object so
// it can pre-fill other fields (name, affiliation) and store the profile_id.

const PlayerSearchInput = ({ value, onChange, onProfileSelect, placeholder, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop]       = useState(false);
  const [loading, setLoading]         = useState(false);
  const timer = useRef(null);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setSuggestions([]); setShowDrop(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await profileApi.search(v.trim());
        setSuggestions(res.data || []);
        setShowDrop(true);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const handleSelect = (profile) => {
    onChange(profile.display_name);
    onProfileSelect(profile);
    setSuggestions([]);
    setShowDrop(false);
  };

  // Parse identifiers string e.g. "bai_id:12345, some_id:xyz"
  const getBaiId = (identifiers) => {
    if (!identifiers) return null;
    const m = identifiers.match(/bai_id:([^\s,]+)/i);
    return m ? m[1] : null;
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        value={value}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setShowDrop(false), 200)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && (
        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.7rem', pointerEvents: 'none' }}>
          searching…
        </span>
      )}
      {showDrop && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #99F6E4', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {suggestions.map(p => {
            const bai = getBaiId(p.identifiers);
            const loc = [p.city, p.state].filter(Boolean).join(', ');
            return (
              <div
                key={p.id}
                onMouseDown={() => handleSelect(p)}
                style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', userSelect: 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F0FDFA'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{p.display_name}</div>
                <div style={{ color: '#6b7280', fontSize: '0.72rem', marginTop: 1 }}>
                  {[bai && `BAI: ${bai}`, loc].filter(Boolean).join(' · ') || 'No identifier on file'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Add Entry Form ───────────────────────────────────────────────────────────

// ─── Manual Draw Setup ───────────────────────────────────────────────────────
const nextPow2 = n => Math.pow(2, Math.ceil(Math.log2(Math.max(n, 2))));

const getEntryName = (entry) => {
  if (!entry) return 'BYE';
  if (entry._partner) return `${entry.player_name} / ${entry._partner.player_name}`;
  return entry.display_name || entry.player_name;
};

const ManualDrawSetup = ({ displayEntries, savedSequence, onSequenceChange, onGenerate, onCancel }) => {
  const playerCount = displayEntries.length;
  const drawSize = nextPow2(playerCount);
  const numByes = drawSize - playerCount;

  const sorted = [...displayEntries].sort((a, b) =>
    getEntryName(a).localeCompare(getEntryName(b))
  );

  const buildDefault = () => [
    ...sorted.map(e => ({ key: e.id, id: e.id, isBye: false })),
    ...Array.from({ length: numByes }, (_, i) => ({ key: `bye-${i}`, id: null, isBye: true })),
  ];

  // Validate savedSequence still matches current entries (entries may have changed)
  const isValidSaved = savedSequence &&
    savedSequence.length === drawSize &&
    displayEntries.every(e => savedSequence.some(s => s.id === e.id));

  const [sequence, setSequence] = useState(() => isValidSaved ? savedSequence : buildDefault());

  const playerById = Object.fromEntries(displayEntries.map(e => [e.id, e]));
  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  // posInputs: transient text values while user is typing in the position box
  const [posInputs, setPosInputs] = useState(() => sequence.map((_, i) => String(i + 1)));

  // Keep posInputs in sync when sequence changes (drag / arrows)
  const syncPosInputs = (newSeq) => setPosInputs(newSeq.map((_, i) => String(i + 1)));

  const updateSequence = (next) => {
    setSequence(next);
    syncPosInputs(next);
    onSequenceChange(next);
  };

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= sequence.length) return;
    const next = [...sequence];
    [next[idx], next[to]] = [next[to], next[idx]];
    updateSequence(next);
  };

  const applyPositions = () => {
    const paired = sequence.map((slot, i) => {
      const pos = Math.min(Math.max(parseInt(posInputs[i], 10) || (i + 1), 1), sequence.length);
      return { pos, slot };
    });
    paired.sort((a, b) => a.pos - b.pos);
    updateSequence(paired.map(p => p.slot));
  };

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOver(idx); };
  const onDrop = (toIdx) => {
    const fromIdx = dragIdx.current;
    if (fromIdx === null || fromIdx === toIdx) { dragIdx.current = null; setDragOver(null); return; }
    const next = [...sequence];
    const [removed] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, removed);
    updateSequence(next);
    dragIdx.current = null;
    setDragOver(null);
  };
  const onDragEnd = () => { dragIdx.current = null; setDragOver(null); };

  const btnBase = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', padding: '1px 4px', borderRadius: 3, lineHeight: 1 };

  return (
    <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151', marginBottom: '0.2rem' }}>Manual Draw Setup</div>
      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.85rem' }}>
        {playerCount} players + {numByes} BYE{numByes !== 1 ? 's' : ''} = {drawSize} slots &nbsp;·&nbsp; drag or use ↑↓ to reorder &nbsp;·&nbsp; draw: 1 vs 2, 3 vs 4, …
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>

        {/* Sequence list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Draw Order</span>
            <button type="button" onClick={applyPositions}
              style={{ fontSize: '0.7rem', padding: '2px 9px', border: '1px solid #0D9488', borderRadius: 4, background: '#F0FDFA', color: '#0D9488', cursor: 'pointer', fontWeight: 600 }}>
              Reorder
            </button>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 420 }}>
            {sequence.map((slot, idx) => {
              const name = slot.isBye ? 'BYE' : getEntryName(playerById[slot.id]);
              const isFirst = idx === 0, isLast = idx === sequence.length - 1;
              const isDropTarget = dragOver === idx;
              return (
                <div key={slot.key}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={e => onDragOver(e, idx)}
                  onDrop={() => onDrop(idx)}
                  onDragEnd={onDragEnd}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.35rem',
                    padding: '4px 7px', borderRadius: 5, marginBottom: 2,
                    background: isDropTarget ? '#e0f2fe' : slot.isBye ? '#f3f4f6' : 'white',
                    border: `1px solid ${isDropTarget ? '#38bdf8' : '#e5e7eb'}`,
                    cursor: 'grab',
                    userSelect: 'none',
                  }}>
                  {/* Drag handle */}
                  <span style={{ color: '#d1d5db', fontSize: '0.75rem', cursor: 'grab', flexShrink: 0 }}>⠿</span>
                  {/* Position input */}
                  <input
                    type="number" min={1} max={sequence.length}
                    value={posInputs[idx] ?? idx + 1}
                    onChange={e => setPosInputs(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    style={{ width: 36, fontSize: '0.72rem', textAlign: 'center', padding: '1px 3px', border: '1px solid #d1d5db', borderRadius: 4, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
                      <span style={{
                        fontSize: '0.8rem', fontWeight: slot.isBye ? 400 : 500,
                        color: slot.isBye ? '#9ca3af' : '#111827', fontStyle: slot.isBye ? 'italic' : 'normal',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{name}</span>
                      {!slot.isBye && playerById[slot.id]?.seed_number && (
                        <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#0D9488', flexShrink: 0 }}>S{playerById[slot.id].seed_number}</span>
                      )}
                    </div>
                    {!slot.isBye && (() => {
                      const entry = playerById[slot.id];
                      const aff = entry?._partner
                        ? [entry.affiliation_name, entry._partner.affiliation_name].filter(Boolean).join(' / ')
                        : entry?.affiliation_name;
                      return aff ? (
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{aff}</div>
                      ) : null;
                    })()}
                  </div>
                  <button type="button" onClick={() => move(idx, -1)} disabled={isFirst}
                    style={{ ...btnBase, color: isFirst ? '#e5e7eb' : '#6b7280' }} title="Move up">↑</button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={isLast}
                    style={{ ...btnBase, color: isLast ? '#e5e7eb' : '#6b7280' }} title="Move down">↓</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Match preview */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Match Preview</div>
          <div style={{ overflowY: 'auto', maxHeight: 420 }}>
            {Array.from({ length: sequence.length / 2 }, (_, mi) => {
              const a = sequence[mi * 2], b = sequence[mi * 2 + 1];
              const n1 = a.isBye ? 'BYE' : getEntryName(playerById[a.id]);
              const n2 = b.isBye ? 'BYE' : getEntryName(playerById[b.id]);
              return (
                <div key={mi} style={{ marginBottom: 4, padding: '5px 8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 5 }}>
                  <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginBottom: 2 }}>Match {mi + 1}</div>
                  <div style={{ fontSize: '0.75rem', color: a.isBye ? '#9ca3af' : '#111827', fontStyle: a.isBye ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n1}</div>
                  <div style={{ fontSize: '0.62rem', color: '#d1d5db', textAlign: 'center' }}>vs</div>
                  <div style={{ fontSize: '0.75rem', color: b.isBye ? '#9ca3af' : '#111827', fontStyle: b.isBye ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n2}</div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onGenerate(sequence.map(s => s.id))}>
          Generate Draw →
        </button>
      </div>
    </div>
  );
};

// ─── Bulk Entry Table ─────────────────────────────────────────────────────────
const BulkEntryTable = ({ event, onSave, onCancel }) => {
  const isDoubles = event.event_type === 'Doubles' || event.event_type === 'Mixed Doubles';
  const emptyRow = () => ({
    _key: Date.now().toString(36) + Math.random().toString(36).slice(2),
    player_name: '', player2_name: '', affiliation_name: '', seed_number: '',
  });
  const [rows, setRows] = useState(() => Array.from({ length: 8 }, emptyRow));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateRow = (idx, field, value) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const removeRow = (idx) =>
    setRows(prev => prev.filter((_, i) => i !== idx));

  const validRows = rows.filter(r =>
    r.player_name.trim() && (!isDoubles || r.player2_name.trim())
  );

  const handleSave = async () => {
    if (validRows.length === 0) { setError('Enter at least one player name'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(validRows.map(r => ({
        player_name: r.player_name.trim(),
        ...(isDoubles && { player2_name: r.player2_name.trim() }),
        affiliation_name: r.affiliation_name.trim() || undefined,
        seed_number: r.seed_number ? parseInt(r.seed_number) : null,
        entry_type: isDoubles ? 'doubles' : 'singles',
      })));
    } catch (err) {
      setError(err.message || 'Failed to save entries');
      setSaving(false);
    }
  };

  const th = { padding: '6px 8px', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', whiteSpace: 'nowrap' };
  const td = { padding: '3px 5px', verticalAlign: 'middle' };
  const inp = { width: '100%', padding: '4px 7px', fontSize: '0.8rem', border: '1px solid #e5e7eb', borderRadius: 4, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' }}>
      {error && <div className="alert alert-error" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>{error}</div>}
      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151', marginBottom: '0.75rem' }}>
        {isDoubles ? 'Add Doubles Pairs' : 'Add Players'}&ensp;
        <span style={{ fontWeight: 400, color: '#6b7280' }}>{validRows.length} entr{validRows.length === 1 ? 'y' : 'ies'} ready</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 28, textAlign: 'center' }}>#</th>
              {isDoubles ? (
                <>
                  <th style={th}>Player 1 *</th>
                  <th style={th}>Player 2 *</th>
                </>
              ) : (
                <th style={th}>Name *</th>
              )}
              <th style={th}>Affiliation</th>
              <th style={{ ...th, width: 70 }}>Seed #</th>
              <th style={{ ...th, width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._key} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                <td style={{ ...td, color: '#9ca3af', textAlign: 'center', fontSize: '0.7rem' }}>{idx + 1}</td>
                {isDoubles ? (
                  <>
                    <td style={td}>
                      <input style={inp} type="text" value={row.player_name} placeholder="Player 1 name"
                        onChange={e => updateRow(idx, 'player_name', e.target.value)} />
                    </td>
                    <td style={td}>
                      <input style={inp} type="text" value={row.player2_name} placeholder="Player 2 name"
                        onChange={e => updateRow(idx, 'player2_name', e.target.value)} />
                    </td>
                  </>
                ) : (
                  <td style={td}>
                    <input style={inp} type="text" value={row.player_name} placeholder="Full name"
                      onChange={e => updateRow(idx, 'player_name', e.target.value)} />
                  </td>
                )}
                <td style={td}>
                  <input style={inp} type="text" value={row.affiliation_name} placeholder="Club / Academy"
                    onChange={e => updateRow(idx, 'affiliation_name', e.target.value)} />
                </td>
                <td style={td}>
                  <input style={{ ...inp, width: 58 }} type="number" min={1} value={row.seed_number} placeholder="—"
                    onChange={e => updateRow(idx, 'seed_number', e.target.value)} />
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <button type="button" onClick={() => removeRow(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '0.85rem', padding: '2px 4px', lineHeight: 1 }}
                    title="Remove row">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setRows(prev => [...prev, emptyRow()])}
          style={{ fontSize: '0.75rem' }}>+ Add Row</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}
          disabled={saving || validRows.length === 0}>
          {saving ? 'Saving…' : `Save ${validRows.length} Entr${validRows.length === 1 ? 'y' : 'ies'}`}
        </button>
      </div>
    </div>
  );
};

// ─── Single Entry Form (used for editing) ────────────────────────────────────
const EntryForm = ({ event, initial, onSaved, onCancel, mode = 'add' }) => {
  const isDoubles = event.event_type === 'Doubles' || event.event_type === 'Mixed Doubles';
  const empty = { player_name: '', player_email: '', player_phone: '', seed_number: '', player2_name: '', player2_email: '', display_name: '', affiliation_name: '', affiliation_id: '', profile_id: null, profile2_id: null };
  const [form, setForm] = useState(initial ? {
    player_name: initial.player_name || '',
    player_email: initial.player_email || '',
    player_phone: initial.player_phone || '',
    seed_number: initial.seed_number || '',
    player2_name: initial.player2_name || '',
    player2_email: initial.player2_email || '',
    display_name: initial.display_name || '',
    affiliation_name: initial.affiliation_name || '',
    affiliation_id: initial.affiliation_id || '',
    profile_id: initial.profile_id || null,
    profile2_id: null,
  } : empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [overrideBWF, setOverrideBWF] = useState(false);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (k === 'seed_number') setOverrideBWF(false); // reset override when seed changes
  };

  const entryCount = event.player_count || 0;
  const bwfInfo = getBWFSeedInfo(entryCount);
  const seedNum = parseInt(form.seed_number) || 0;
  const exceedsBWF = seedNum > 0 && seedNum > bwfInfo.max;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.player_name.trim()) { setError('Player name is required'); return; }
    if (isDoubles && !form.player2_name.trim()) { setError('Partner name is required for doubles'); return; }
    if (exceedsBWF && !overrideBWF) { setError(`BWF Guidelines allow max ${bwfInfo.max} seeds for ${entryCount} entries. Check "Override" to proceed.`); return; }
    setSaving(true);
    try {
      await onSaved({
        ...form,
        seed_number: form.seed_number ? parseInt(form.seed_number) : null,
        entry_type: isDoubles ? 'doubles' : 'singles',
        profile_id: form.profile_id || undefined,
      });
      if (mode === 'add') setForm(empty);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${mode === 'add' ? 'add' : 'update'} entry`);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' }}>
      {error && <div className="alert alert-error" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>{error}</div>}
      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151', marginBottom: '0.75rem' }}>
        {mode === 'edit' ? 'Edit Entry' : (isDoubles ? 'Add Doubles Pair' : 'Add Player')}
      </div>

      <div className="grid grid-2" style={{ marginBottom: '0.5rem' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem' }}>
            {isDoubles ? 'Player 1 Name *' : 'Name *'}
            {form.profile_id && <span style={{ marginLeft: 6, color: '#059669', fontSize: '0.7rem', fontWeight: 400 }}>✓ linked to profile</span>}
          </label>
          {mode === 'add' ? (
            <PlayerSearchInput
              value={form.player_name}
              onChange={v => setForm(prev => ({ ...prev, player_name: v, profile_id: null }))}
              onProfileSelect={p => setForm(prev => ({ ...prev, player_name: p.display_name, profile_id: p.id }))}
              placeholder="Type to search existing players…"
            />
          ) : (
            <input type="text" className="form-input" value={form.player_name}
              onChange={e => set('player_name', e.target.value)} placeholder="Full name" />
          )}
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem' }}>{isDoubles ? 'Player 1 Email' : 'Email'}</label>
          <input type="email" className="form-input" value={form.player_email}
            onChange={e => set('player_email', e.target.value)} placeholder="email@example.com" />
        </div>
      </div>

      {isDoubles && (
        <div className="grid grid-2" style={{ marginBottom: '0.5rem' }}>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: '0.75rem' }}>
              Partner Name *
              {form.profile2_id && <span style={{ marginLeft: 6, color: '#059669', fontSize: '0.7rem', fontWeight: 400 }}>✓ linked to profile</span>}
            </label>
            {mode === 'add' ? (
              <PlayerSearchInput
                value={form.player2_name}
                onChange={v => setForm(prev => ({ ...prev, player2_name: v, profile2_id: null }))}
                onProfileSelect={p => setForm(prev => ({ ...prev, player2_name: p.display_name, profile2_id: p.id }))}
                placeholder="Type to search existing players…"
              />
            ) : (
              <input type="text" className="form-input" value={form.player2_name}
                onChange={e => set('player2_name', e.target.value)} placeholder="Partner full name" />
            )}
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: '0.75rem' }}>Partner Email</label>
            <input type="email" className="form-input" value={form.player2_email}
              onChange={e => set('player2_email', e.target.value)} placeholder="partner@example.com" />
          </div>
        </div>
      )}

      <div className="grid grid-2" style={{ marginBottom: '0.5rem' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem' }}>Phone</label>
          <input type="text" className="form-input" value={form.player_phone}
            onChange={e => set('player_phone', e.target.value)} placeholder="+1234567890" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Seed #</span>
            <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.7rem' }}>BWF max: <strong>{bwfInfo.max}</strong> ({entryCount} entries)</span>
          </label>
          <input type="number" className="form-input" value={form.seed_number} min={1}
            onChange={e => set('seed_number', e.target.value)} placeholder="Leave blank = unseeded"
            style={exceedsBWF ? { borderColor: '#f59e0b', background: '#fffbeb' } : {}} />
          {exceedsBWF && (
            <div style={{ marginTop: '0.4rem', padding: '0.5rem 0.65rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: '0.75rem', color: '#92400e' }}>
              ⚠️ BWF allows max <strong>{bwfInfo.max}</strong> seeds for {entryCount} entries ({bwfInfo.label}).
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={overrideBWF} onChange={e => setOverrideBWF(e.target.checked)} />
                Override BWF recommendation
              </label>
            </div>
          )}
        </div>
      </div>

      {isDoubles && (
        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
          <label className="form-label" style={{ fontSize: '0.75rem' }}>Display Name (pair)</label>
          <input type="text" className="form-input" value={form.display_name}
            onChange={e => set('display_name', e.target.value)}
            placeholder={`${form.player_name || 'P1'} / ${form.player2_name || 'P2'}`} />
        </div>
      )}

      <div className="grid grid-2" style={{ marginBottom: '0.5rem' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem' }}>Affiliation <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="text" className="form-input" value={form.affiliation_name}
            onChange={e => set('affiliation_name', e.target.value)}
            placeholder="Club, Academy, District, State…" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.75rem' }}>Affiliation ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="text" className="form-input" value={form.affiliation_id}
            onChange={e => set('affiliation_id', e.target.value)}
            placeholder="BWF ID, BAI ID, ITF ID…" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
          {saving ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Add Entry')}
        </button>
      </div>
    </form>
  );
};

// ─── Knockout Bracket (mirrors DrawTab visual) ────────────────────────────────

const KnockoutBracket = ({ matches, getRoundName, qualifyingPlayerIds = new Set() }) => {
  const rounds = [...new Set(matches.map(m => m.round_number))];
  const sortedRounds = [...rounds].sort((a, b) => parseInt(a) - parseInt(b));
  if (sortedRounds.length === 0) return null;

  const firstRoundCount = matches.filter(m => m.round_number === sortedRounds[0]).length;
  const BASE_SLOT = 120;
  const totalHeight = firstRoundCount * BASE_SLOT;

  return (
    <div style={{ display: 'flex', overflowX: 'auto', padding: '20px 10px', background: '#fcfcfc', borderRadius: 8 }}>
      {sortedRounds.map((roundNum, roundIdx) => {
        const roundMatches = matches.filter(m => m.round_number === roundNum).sort((a, b) => a.match_number - b.match_number);
        const slotHeight = roundMatches.length > 0 ? totalHeight / roundMatches.length : totalHeight;
        return (
          <React.Fragment key={roundNum}>
            {roundIdx > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', width: '40px', flexShrink: 0 }}>
                <div style={{ visibility: 'hidden', textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.85rem', margin: '0 4px 8px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {getRoundName(roundNum)}
                </div>
                <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                  {roundMatches.map((m, idx) => (
                    <div key={`conn-${m.id}`} style={{ position: 'absolute', top: `${idx * slotHeight}px`, height: `${slotHeight}px`, width: '100%' }}>
                      <div style={{ position: 'absolute', left: '50%', top: '25%', bottom: '25%', borderLeft: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '50%', right: '-4px', top: '50%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '25%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '75%', borderTop: '2px solid #adb5bd' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '200px', flexShrink: 0 }}>
              <div style={{ textAlign: 'center', padding: '8px', background: 'var(--primary-color)', color: 'white', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', margin: '0 4px 8px' }}>
                {getRoundName(roundNum)}
              </div>
              <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                {roundMatches.map((match, idx) => (
                  <div key={match.id} style={{ position: 'absolute', top: `${(idx + 0.5) * slotHeight}px`, transform: 'translateY(-50%)', left: '4px', right: '4px' }}>
                    <div style={{ width: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <div style={{ border: match.winner_id ? '2px solid #28a745' : '1px solid #dee2e6', borderRadius: '6px', background: match.winner_id ? '#f8fff9' : 'white', overflow: 'hidden' }}>
                        <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1', background: (!match.player1_id && match.qualifier_slot) ? '#F0FDFA' : 'transparent' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: (match.winner_id && match.winner_id === match.player1_id) || match.player1_seed ? 'bold' : 'normal', color: (match.winner_id && match.winner_id === match.player1_id) ? '#28a745' : (!match.player1_id && match.qualifier_slot ? '#0F766E' : '#333') }}>
                            {match.player1_display || match.player1_name || (!match.player1_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : (match.notes?.toUpperCase().includes('BYE') ? 'BYE' : 'TBD'))}
                            {match.player1_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px' }}>[{match.player1_seed}]</span> : ''}
                            {match.player1_id && qualifyingPlayerIds.has(match.player1_id) ? <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: '3px', fontWeight: 600 }}>[Q]</span> : ''}
                          </span>
                        </div>
                        <div style={{ padding: '8px 10px', background: (!match.player2_id && match.qualifier_slot) ? '#F0FDFA' : 'transparent' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: (match.winner_id && match.winner_id === match.player2_id) || match.player2_seed ? 'bold' : 'normal', color: (match.winner_id && match.winner_id === match.player2_id) ? '#28a745' : (!match.player2_id && match.qualifier_slot ? '#0F766E' : '#333') }}>
                            {match.player2_display || match.player2_name || (!match.player2_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : (match.notes?.toUpperCase().includes('BYE') ? 'BYE' : 'TBD'))}
                            {match.player2_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px' }}>[{match.player2_seed}]</span> : ''}
                            {match.player2_id && qualifyingPlayerIds.has(match.player2_id) ? <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: '3px', fontWeight: 600 }}>[Q]</span> : ''}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px' }}>
                        <span style={{ fontSize: '0.65rem', color: '#999' }}>
                          {match.notes === '3rd Place Playoff' ? '3rd Place' : `M${match.match_number}`}
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#999' }}>
                          {match.scheduled_time ? `${new Date(match.scheduled_time).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(match.scheduled_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Pair Seed Edit Row ───────────────────────────────────────────────────────
// Inline table-row that lets organizers edit the seed number for a doubles pair.
// Only seed_number is editable; player names / affiliations are read-only for pairs.

const PairSeedEditRow = ({ displayRow, colSpan, onSave, onCancel }) => {
  const [seed, setSeed] = useState(displayRow.seed_number ?? '');
  return (
    <tr style={{ background: '#F0FDFA' }}>
      <td colSpan={colSpan} style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.82rem', color: '#4b5563', fontWeight: 500 }}>
            Seed for pair&nbsp;
            <strong>{displayRow.player_name}{displayRow._partner ? ` / ${displayRow._partner.player_name}` : ''}</strong>:
          </span>
          <input
            type="number"
            min="1"
            placeholder="—"
            value={seed}
            onChange={e => setSeed(e.target.value)}
            style={{ width: 64, padding: '2px 6px', border: '1px solid #99F6E4', borderRadius: 4, fontSize: '0.85rem' }}
          />
          <button
            onClick={() => onSave(seed)}
            style={{ background: '#0D9488', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: '0.82rem', cursor: 'pointer' }}>
            Save
          </button>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 10px', fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Event Card ───────────────────────────────────────────────────────────────

// Section badge colours
const SECTION_STYLE = {
  main:       { bg: '#dbeafe', color: '#1d4ed8', label: 'Main' },
  qualifying: { bg: '#fef3c7', color: '#92400e', label: 'Qual' },
  reserve:    { bg: '#f3f4f6', color: '#6b7280', label: 'Rsv' },
};

const SectionBadge = ({ section }) => {
  const s = SECTION_STYLE[section] || SECTION_STYLE.main;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '1px 7px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const EventCard = ({ event, tournament, isOrganizer, onChanged, onDeleted, allEvents = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showManualDraw, setShowManualDraw] = useState(false);
  const [manualDrawSeq, setManualDrawSeq] = useState(null); // persisted sequence across open/close
  const [editingId, setEditingId] = useState(null);       // player id being edited (singles)
  const [editingSeedPairId, setEditingSeedPairId] = useState(null); // pair_id being seed-edited (doubles)
  const [generatingDraw, setGeneratingDraw] = useState(false);
  const [generatingDummy, setGeneratingDummy] = useState(false);
  const [dummySection, setDummySection] = useState('main'); // 'main' | 'qualifying'
  const [scoreTarget, setScoreTarget] = useState(null);

  // Memoized dummy size buttons — recomputed only when section/event config changes
  const dummySizeList = useMemo(() => {
    const isQualSection = event.has_qualifying && dummySection === 'qualifying';
    const isMainSection = event.has_qualifying && dummySection === 'main';
    const qSlots = event.qualifying_spots || 0;
    const sizeLimit = isQualSection ? (event.qualifying_draw_size || 256) : (event.max_participants || 256);
    return DUMMY_SIZES
      .filter(n => n <= sizeLimit && (!isMainSection || n > qSlots))
      .map(n => ({
        size: n,
        label: isMainSection ? `${n} (${n - qSlots})` : `${n}`,
        title: isMainSection
          ? `Total draw: ${n}  •  Main players: ${n - qSlots}  •  Q slots: ${qSlots}`
          : `Generate ${n} players`,
      }));
  }, [event.has_qualifying, event.qualifying_draw_size, event.max_participants, event.qualifying_spots, dummySection]);
  const [deletingEvent, setDeletingEvent] = useState(false);
  // Qualifying state
  const [generatingQualDraw, setGeneratingQualDraw] = useState(false);
  const [qualSlots, setQualSlots] = useState(null);          // { slots, allQualifyingWinnersKnown }
  const [assigningQual, setAssigningQual] = useState(false);
  const [qualPreview, setQualPreview] = useState(null);      // dry-run preview array
  const [pfqLoading, setPfqLoading] = useState(false);
  const [pfrLoading, setPfrLoading] = useState(null);        // match_id being filled
  const [pfqOpen, setPfqOpen] = useState(false);
  const [pfqSelected, setPfqSelected] = useState('');

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await eventApi.listEntries(tournament.id, event.id);
      setEntries(res.data);
    } catch {} finally { setLoadingEntries(false); }
  }, [tournament.id, event.id]);

  // Collapse doubles pairs (same pair_id) into a single display row.
  const displayEntries = (() => {
    const seen = new Set();
    const result = [];
    for (const p of entries) {
      if (p.pair_id) {
        if (seen.has(p.pair_id)) continue;
        seen.add(p.pair_id);
        const partner = entries.find(q => q.pair_id === p.pair_id && q.id !== p.id);
        result.push({ ...p, _partner: partner || null });
      } else {
        result.push(p);
      }
    }
    return result;
  })();

  const loadMatches = useCallback(async () => {
    setLoadingMatches(true);
    try {
      const [mRes, sRes] = await Promise.all([
        eventApi.getMatches(tournament.id, event.id),
        event.draw_type === 'league' ? eventApi.getStandings(tournament.id, event.id) : Promise.resolve({ data: [] }),
      ]);
      setMatches(mRes.data);
      setStandings(sRes.data);
    } catch {} finally { setLoadingMatches(false); }
  }, [tournament.id, event.id, event.draw_type]);

  useEffect(() => {
    if (expanded) {
      loadEntries();
      if (event.status !== 'registration' && event.status !== 'seeding') loadMatches();
    }
  }, [expanded, loadEntries, loadMatches, event.status]);

  const handleGenerateDraw = async () => {
    const hasDraw = matches.length > 0;
    if (hasDraw && !window.confirm(`Regenerate draw for "${event.name}"? Existing draw will be overwritten.`)) return;
    setGeneratingDraw(true);
    try {
      await eventApi.generateDraw(tournament.id, event.id);
      await loadMatches();
      setView('draw');
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate draw');
    } finally { setGeneratingDraw(false); }
  };

  const handleGenerateDummy = async (count) => {
    const section = event.has_qualifying ? dummySection : 'main';
    setGeneratingDummy(true);
    try {
      await eventApi.generateDummy(tournament.id, event.id, { count, draw_section: section });
      await loadEntries();
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate dummy entries');
    } finally { setGeneratingDummy(false); }
  };

  const handleClearEntries = async () => {
    if (!window.confirm(`Delete ALL ${entries.length} entries from "${event.name}"? The draw will also be cleared. This cannot be undone.`)) return;
    try {
      await eventApi.clearEntries(tournament.id, event.id);
      await loadEntries();
      await loadMatches();
      onChanged();
    } catch (err) { alert(err.response?.data?.error || 'Failed to clear entries'); }
  };

  const handleAddEntry = async (data) => {
    await eventApi.addEntry(tournament.id, event.id, data);
    await loadEntries();
    onChanged();
    setShowAddEntry(false);
  };

  const handleBulkAddEntries = async (entriesData) => {
    for (const data of entriesData) {
      await eventApi.addEntry(tournament.id, event.id, data);
    }
    await loadEntries();
    onChanged();
    setShowBulkAdd(false);
  };

  const handleMoveEntry = async (displayRow, direction) => {
    const idx = displayEntries.findIndex(e => e.id === displayRow.id);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === displayEntries.length - 1) return;
    const newOrder = [...displayEntries];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    const updates = [];
    newOrder.forEach((entry, i) => {
      const s = (i + 1) * 10;
      updates.push({ id: entry.id, sort_order: s });
      if (entry._partner) updates.push({ id: entry._partner.id, sort_order: s });
    });
    try {
      await eventApi.reorderEntries(tournament.id, event.id, updates);
      await loadEntries();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reorder');
    }
  };

  const handleGenerateManualDraw = async (slots) => {
    const hasDraw = matches.length > 0;
    if (hasDraw && !window.confirm('Replace existing draw with this manual draw?')) return;
    try {
      await eventApi.generateManualDraw(tournament.id, event.id, { slots });
      await loadMatches();
      setShowManualDraw(false);
      setView('draw');
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate manual draw');
    }
  };

  const handleEditEntry = async (playerId, data) => {
    await eventApi.updateEntry(tournament.id, event.id, playerId, data);
    await loadEntries();
    setEditingId(null);
  };

  const handleEditPairSeed = async (displayRow, seedNumber) => {
    const seed = seedNumber === '' ? null : parseInt(seedNumber, 10) || null;
    await Promise.all([
      eventApi.updateEntry(tournament.id, event.id, displayRow.id, { seed_number: seed }),
      displayRow._partner && eventApi.updateEntry(tournament.id, event.id, displayRow._partner.id, { seed_number: seed }),
    ].filter(Boolean));
    await loadEntries();
    setEditingSeedPairId(null);
  };

  const handleDeleteEntry = async (displayRow) => {
    const isPair = !!displayRow._partner;
    const name = isPair
      ? `${displayRow.player_name} / ${displayRow._partner.player_name}`
      : (displayRow.display_name || displayRow.player_name);
    if (!window.confirm(`Remove "${name}" from this event?`)) return;
    try {
      if (isPair) {
        await Promise.all([
          eventApi.removeEntry(tournament.id, event.id, displayRow.id),
          eventApi.removeEntry(tournament.id, event.id, displayRow._partner.id),
        ]);
      } else {
        await eventApi.removeEntry(tournament.id, event.id, displayRow.id);
      }
      loadEntries();
      onChanged();
    } catch (err) { alert(err.response?.data?.error || 'Failed to remove entry'); }
  };

  const handleScoreWinner = async (matchId, winnerId) => {
    try {
      await tournamentApi.updateMatch(matchId, { winner_id: winnerId, status: 'completed' });
      loadMatches();
      setScoreTarget(null);
      // Refresh Q-slot status in case this was the qualifying final
      if (event.has_qualifying) loadQualSlots();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update match'); }
  };

  const getRoundName = (roundNumber) => {
    if (event.draw_type === 'league') return `Round ${roundNumber}`;
    const nonPlayoff = matches.filter(m => m.notes !== '3rd Place Playoff');
    const cnt = nonPlayoff.filter(m => m.round_number === roundNumber).length;
    if (cnt === 1) return 'Final';
    if (cnt === 2) return 'Semi Finals';
    if (cnt === 4) return 'Quarter Finals';
    if (cnt === 8) return 'Round of 16';
    return `Round of ${cnt * 2}`;
  };

  const rounds = [...new Set(matches.map(m => m.round_number))].sort((a, b) =>
    event.draw_type === 'league' ? a - b : b - a
  );

  const handleDeleteEvent = async (e) => {
    e.stopPropagation(); // don't toggle expand
    if (!window.confirm(`Delete event "${event.name}"? This will also remove all entries and the draw. This cannot be undone.`)) return;
    setDeletingEvent(true);
    try {
      await eventApi.delete(tournament.id, event.id);
      onDeleted(event.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
      setDeletingEvent(false);
    }
  };

  // ── Qualifying handlers ──────────────────────────────────────────────────────

  const [loadingQualSlots, setLoadingQualSlots] = useState(false);
  const loadQualSlots = useCallback(async () => {
    if (!event.has_qualifying) return;
    setLoadingQualSlots(true);
    try {
      const res = await eventApi.getQualifierSlots(tournament.id, event.id);
      setQualSlots(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load qualifier status');
    } finally {
      setLoadingQualSlots(false);
    }
  }, [tournament.id, event.id, event.has_qualifying]);

  const handleGenerateMainDrawWithSlots = async () => {
    if (matches.length > 0 && !window.confirm('Regenerate main draw with Q-slot placeholders? Existing draw will be overwritten.')) return;
    setGeneratingDraw(true);
    try {
      const res = await eventApi.generateMainDrawWithQSlots(tournament.id, event.id);
      await loadMatches();
      await loadQualSlots();
      setView('draw');
      onChanged();
      if (res.data?.autoAssigned) toast.success('Qualifying winners auto-assigned to Q slots');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate main draw');
    } finally { setGeneratingDraw(false); }
  };

  const handleGenerateQualDraw = async () => {
    if (!window.confirm('Generate qualifying draw? This will create a separate qualifying event for the qualifying-section players.')) return;
    setGeneratingQualDraw(true);
    try {
      await eventApi.generateQualifyingDraw(tournament.id, event.id);
      toast.success('Qualifying draw generated');
      onChanged();
      await loadQualSlots();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate qualifying draw';
      const hint = msg.includes('at least 2 qualifying')
        ? `${msg}\n\nMake sure players are assigned to the "Qualifying" section first (use the Section dropdown in the Entries tab).`
        : msg;
      alert(hint);
    } finally { setGeneratingQualDraw(false); }
  };

  const handlePreviewQualAssign = async () => {
    try {
      const res = await eventApi.previewQualifierAssignment(tournament.id, event.id);
      setQualPreview(res.data.assignments);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to preview assignment');
    }
  };

  const handlePFQ = async (playerId, playerName) => {
    setPfqLoading(true);
    try {
      const res = await eventApi.promoteFromQualifying(tournament.id, event.id, playerId);
      toast.success(`${playerName} withdrawn · ${res.data.promoted_player.player_name} promoted from qualifying`);
      setPfqOpen(false);
      setPfqSelected('');
      loadMatches();
      loadQualSlots();
    } catch (err) {
      toast.error(err.response?.data?.error || 'PFQ promotion failed');
    } finally { setPfqLoading(false); }
  };

  const handlePFR = async (matchId, position) => {
    if (!window.confirm('Fill this vacant slot with the top reserve player?')) return;
    setPfrLoading(matchId + position);
    try {
      const res = await eventApi.promoteFromReserve(tournament.id, event.parent_event_id, matchId, position);
      toast.success(`${res.data.promoted_player.player_name} promoted from reserve`);
      loadMatches();
    } catch (err) {
      toast.error(err.response?.data?.error || 'PFR promotion failed');
    } finally { setPfrLoading(null); }
  };

  const handleConfirmQualAssign = async () => {
    setAssigningQual(true);
    try {
      await eventApi.assignQualifiers(tournament.id, event.id);
      toast.success('Qualifiers assigned to main draw');
      setQualPreview(null);
      await loadMatches();
      await loadQualSlots();
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign qualifiers');
    } finally { setAssigningQual(false); }
  };

  const handleUpdateSection = async (playerId, section) => {
    try {
      await eventApi.updatePlayerSection(tournament.id, event.id, playerId, { draw_section: section });
      await loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update section');
    }
  };

  // Load Q-slot status whenever draw tab is opened for qualifying events
  useEffect(() => {
    if (expanded && view === 'draw' && event.has_qualifying) loadQualSlots();
  }, [expanded, view, loadQualSlots, event.has_qualifying]);

  const hasDraw = matches.length > 0;
  const canDraw = entries.length >= 2;
  const isLeague = event.draw_type === 'league';
  const isMainDraw = !!event.has_qualifying;
  const isQualifyingChild = !!event.parent_event_id;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: 'white', marginBottom: '0.75rem', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.875rem 1rem', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{event.name}</span>
            <Badge status={event.status} />
            {!!event.has_qualifying && (() => {
              const hasQualChild = allEvents.some(e => e.parent_event_id === event.id && e.event_stage === 'Qualifying');
              return (
                <span style={{ background: '#CCFBF1', color: '#5b21b6', padding: '1px 7px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600 }}>
                  {hasQualChild ? 'Main' : 'Qualifying'}
                </span>
              );
            })()}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
            {DRAW_TYPE_LABELS[event.draw_type] || event.draw_type}
            {!event.parent_event_id && (<>{' · '}{event.player_count || 0}/{event.max_participants} entries</>)}
            {event.entry_fee > 0 ? ` · $${event.entry_fee}` : ''}
          </div>
        </div>
        {isOrganizer && (
          <button
            onClick={handleDeleteEvent}
            disabled={deletingEvent}
            title="Delete event"
            style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', color: '#9ca3af', padding: '3px 6px', fontSize: '0.85rem', lineHeight: 1, flexShrink: 0, opacity: deletingEvent ? 0.4 : 1 }}
            onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.background = '#fff5f5'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'none'; }}>
            🗑
          </button>
        )}
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none"
          style={{ color: '#9ca3af', transition: 'transform 0.15s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '1rem' }}>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: '1rem', background: '#f3f4f6', borderRadius: 8, padding: 3, width: 'fit-content' }}>
            {['entries', 'draw'].map(t => (
              <button key={t} onClick={() => { setView(t); if (t === 'draw') loadMatches(); }}
                style={{ padding: '4px 14px', fontSize: '0.8rem', fontWeight: 500, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: view === t ? 'white' : 'transparent',
                  color: view === t ? '#0D9488' : '#6b7280',
                  boxShadow: view === t ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
                {t === 'entries' ? ` (${displayEntries.length})` : ''}
                {t === 'draw' && hasDraw ? ` (${matches.length} matches)` : ''}
              </button>
            ))}
          </div>

          {/* ── Entries ── */}
          {view === 'entries' && (
            <>
              {loadingEntries ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0' }}>Loading entries...</div>
              ) : entries.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1.25rem 0' }}>
                  No entries yet.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
                  <table className="table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>#</th>
                        <th style={{ maxWidth: 160, width: 160 }}>Name</th>
                        <th style={{ maxWidth: 140, width: 140 }}>Email</th>
                        <th style={{ maxWidth: 130, width: 130 }}>Affiliation</th>
                        <th style={{ width: 55 }}>Seed</th>
                        {isMainDraw && <th style={{ width: 80 }}>Section</th>}
                        <th style={{ width: 80 }}>Status</th>
                        {isOrganizer && <th style={{ width: 150 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {displayEntries.map((p, i) => {
                        const isPair = !!p._partner;
                        const displayName = isPair
                          ? `${p.player_name} / ${p._partner.player_name}`
                          : (p.display_name || p.player_name);
                        const affiliation = isPair
                          ? [p.affiliation_name, p._partner.affiliation_name].filter(Boolean).join(' / ') || '—'
                          : (p.affiliation_name || '—');
                        return (
                          <React.Fragment key={p.id}>
                            <tr>
                              <td style={{ color: '#9ca3af' }}>{i + 1}</td>
                              <td style={{ fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {isPair ? (
                                  // Two-row model: each player has their own row and profile
                                  <>
                                    {p.profile_id
                                      ? <a href={`/profiles/${p.profile_id}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{p.player_name}</a>
                                      : p.player_name}
                                    {' / '}
                                    {p._partner.profile_id
                                      ? <a href={`/profiles/${p._partner.profile_id}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{p._partner.player_name}</a>
                                      : p._partner.player_name}
                                  </>
                                ) : p.entry_type === 'doubles' && p.player2_name ? (
                                  // Legacy single-row model: only player1 has a profile link
                                  <>
                                    {p.profile_id
                                      ? <a href={`/profiles/${p.profile_id}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{p.player_name}</a>
                                      : p.player_name}
                                    {' / '}
                                    {p.player2_name}
                                  </>
                                ) : (
                                  p.profile_id
                                    ? <a href={`/profiles/${p.profile_id}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{displayName}</a>
                                    : displayName
                                )}
                              </td>
                              <td style={{ color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isPair ? '—' : (p.player_email || '—')}</td>
                              <td style={{ color: '#6b7280', fontSize: '0.8rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{affiliation}</td>
                              <td style={{ color: '#6b7280' }}>
                                {p.seed_number ? <span style={{ fontWeight: 600, color: '#0D9488' }}>S{p.seed_number}</span> : '—'}
                              </td>
                              {isMainDraw && (
                                <td>
                                  {isOrganizer ? (
                                    <select
                                      value={p.draw_section || 'main'}
                                      onChange={e => handleUpdateSection(p.id, e.target.value)}
                                      style={{ fontSize: '0.72rem', padding: '1px 4px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', background: 'white' }}>
                                      <option value="main">Main</option>
                                      <option value="qualifying">Qual</option>
                                      <option value="reserve">Reserve</option>
                                    </select>
                                  ) : (
                                    <SectionBadge section={p.draw_section || 'main'} />
                                  )}
                                </td>
                              )}
                              <td>
                                {p.status === 'pending' && (
                                  <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 7px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600 }}>Pending</span>
                                )}
                                {p.status === 'waitlisted' && (
                                  <span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 7px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600 }}>Waitlisted</span>
                                )}
                                {p.status === 'withdrawn' && (
                                  <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '1px 7px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600 }}>Withdrawn</span>
                                )}
                              </td>
                              {isOrganizer && (
                                <td>
                                  <div style={{ display: 'flex', gap: 3, flexWrap: 'nowrap', alignItems: 'center' }}>
                                    <button onClick={() => handleMoveEntry(p, 'up')} disabled={i === 0}
                                      style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#e5e7eb' : '#9ca3af', fontSize: '0.75rem', padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                                      title="Move up">↑</button>
                                    <button onClick={() => handleMoveEntry(p, 'down')} disabled={i === displayEntries.length - 1}
                                      style={{ background: 'none', border: 'none', cursor: i === displayEntries.length - 1 ? 'default' : 'pointer', color: i === displayEntries.length - 1 ? '#e5e7eb' : '#9ca3af', fontSize: '0.75rem', padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                                      title="Move down">↓</button>
                                    {p.status === 'pending' && (
                                      <>
                                        <button
                                          onClick={async () => { try { await tournamentApi.approvePlayer(tournament.id, p.id); toast.success('Approved'); loadEntries(); } catch { toast.error('Failed to approve'); } }}
                                          style={{ background: '#dcfce7', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}
                                          title="Approve">✓</button>
                                        <button
                                          onClick={async () => { try { await tournamentApi.rejectPlayer(tournament.id, p.id); toast.success('Rejected'); loadEntries(); } catch { toast.error('Failed to reject'); } }}
                                          style={{ background: '#fee2e2', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}
                                          title="Reject">✕</button>
                                      </>
                                    )}
                                    {p.status === 'waitlisted' && (
                                      <button
                                        onClick={async () => { try { await tournamentApi.rejectPlayer(tournament.id, p.id); toast.success('Removed from waitlist'); loadEntries(); } catch { toast.error('Failed'); } }}
                                        style={{ background: '#fee2e2', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4 }}
                                        title="Remove">✕</button>
                                    )}
                                    {['registered', 'confirmed'].includes(p.status) && (
                                      <button
                                        onClick={async () => { if (!window.confirm('Withdraw this player?')) return; try { await tournamentApi.withdrawPlayer(tournament.id, p.id); toast.success('Withdrawn'); loadEntries(); } catch { toast.error('Failed to withdraw'); } }}
                                        style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4 }}
                                        title="Withdraw">W</button>
                                    )}
                                    {isPair ? (
                                      <button
                                        onClick={() => setEditingSeedPairId(editingSeedPairId === p.pair_id ? null : p.pair_id)}
                                        style={{ background: editingSeedPairId === p.pair_id ? '#F0FDFA' : 'none', border: 'none', cursor: 'pointer', color: '#0D9488', fontSize: '0.8rem', padding: '2px 6px', borderRadius: 4 }}
                                        title="Edit seed">✎</button>
                                    ) : (
                                      <button
                                        onClick={() => setEditingId(editingId === p.id ? null : p.id)}
                                        style={{ background: editingId === p.id ? '#F0FDFA' : 'none', border: 'none', cursor: 'pointer', color: '#0D9488', fontSize: '0.8rem', padding: '2px 6px', borderRadius: 4 }}
                                        title="Edit">✎</button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteEntry(p)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem', padding: '2px 6px', borderRadius: 4 }}
                                      title="Remove">🗑</button>
                                  </div>
                                </td>
                              )}
                            </tr>
                            {editingId === p.id && (
                              <tr>
                                <td colSpan={isOrganizer ? 6 : 5} style={{ padding: 0, borderBottom: 'none' }}>
                                  <EntryForm
                                    event={event}
                                    initial={p}
                                    mode="edit"
                                    onSaved={(data) => handleEditEntry(p.id, data)}
                                    onCancel={() => setEditingId(null)}
                                  />
                                </td>
                              </tr>
                            )}
                            {isPair && editingSeedPairId === p.pair_id && (
                              <PairSeedEditRow
                                displayRow={p}
                                colSpan={isOrganizer ? 6 : 5}
                                onSave={(seed) => handleEditPairSeed(p, seed)}
                                onCancel={() => setEditingSeedPairId(null)}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Organizer actions */}
              {isOrganizer && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* Add entry / add multiple forms */}
                  {showAddEntry && (
                    <EntryForm
                      event={event}
                      mode="add"
                      onSaved={handleAddEntry}
                      onCancel={() => setShowAddEntry(false)}
                    />
                  )}
                  {showBulkAdd && (
                    <BulkEntryTable
                      event={event}
                      onSave={handleBulkAddEntries}
                      onCancel={() => setShowBulkAdd(false)}
                    />
                  )}
                  {showManualDraw && (
                    <ManualDrawSetup
                      displayEntries={displayEntries}
                      savedSequence={manualDrawSeq}
                      onSequenceChange={setManualDrawSeq}
                      onGenerate={handleGenerateManualDraw}
                      onCancel={() => setShowManualDraw(false)}
                    />
                  )}
                  {!showAddEntry && !showBulkAdd && !showManualDraw && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowAddEntry(true)}>+ Add Entry</button>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowBulkAdd(true)}>+ Add Multiple</button>

                      {/* Dummy generate picker */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Generate:</span>
                        {event.has_qualifying && ['main', 'qualifying'].map(s => (
                          <button key={s}
                            onClick={() => setDummySection(s)}
                            style={{
                              padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                              border: '1px solid', borderRadius: 4, cursor: 'pointer',
                              borderColor: dummySection === s ? '#0D9488' : '#d1d5db',
                              background: dummySection === s ? '#F0FDFA' : 'white',
                              color: dummySection === s ? '#0D9488' : '#6b7280',
                            }}>
                            {s === 'main' ? 'Main' : 'Qual'}
                          </button>
                        ))}
                        {dummySizeList.map(({ size, label, title }) => (
                          <button key={size}
                            onClick={() => handleGenerateDummy(size)}
                            disabled={generatingDummy}
                            title={title}
                            style={{ padding: '3px 8px', fontSize: '0.75rem', fontWeight: 500, border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', background: 'white', color: '#374151', opacity: generatingDummy ? 0.5 : 1 }}>
                            {label}
                          </button>
                        ))}
                      </div>

                      {/* Delete all entries */}
                      {entries.length > 0 && (
                        <button
                          onClick={handleClearEntries}
                          style={{ padding: '3px 10px', fontSize: '0.75rem', fontWeight: 500, border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', background: 'white', color: '#dc2626' }}>
                          Delete All
                        </button>
                      )}

                      {/* Generate / Regenerate draw */}
                      {canDraw && !isMainDraw && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                          {tournament.manual_draw_enabled && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setShowManualDraw(true)}
                              style={{ color: '#6b7280' }}>
                              Manual Draw
                            </button>
                          )}
                          <button
                            className={hasDraw ? 'btn btn-outline btn-sm' : 'btn btn-primary btn-sm'}
                            onClick={handleGenerateDraw}
                            disabled={generatingDraw}>
                            {generatingDraw ? 'Generating...' : (hasDraw ? '↺ Regenerate Draw' : 'Generate Draw →')}
                          </button>
                        </div>
                      )}
                      {isMainDraw && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                          <button
                            className={hasDraw ? 'btn btn-outline btn-sm' : 'btn btn-primary btn-sm'}
                            onClick={handleGenerateMainDrawWithSlots}
                            disabled={generatingDraw}>
                            {generatingDraw ? 'Generating...' : (hasDraw ? '↺ Main Draw' : 'Gen. Main Draw →')}
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={handleGenerateQualDraw}
                            disabled={generatingQualDraw}
                            style={{ color: '#92400e', borderColor: '#fde68a' }}>
                            {generatingQualDraw ? '...' : 'Gen. Qual. Draw'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Draw ── */}
          {view === 'draw' && (
            <>
              {/* Draw action bar */}
              {isOrganizer && event.status !== 'completed' && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {canDraw ? (
                    isMainDraw ? (
                      <>
                        <button className={hasDraw ? 'btn btn-outline btn-sm' : 'btn btn-primary btn-sm'}
                          onClick={handleGenerateMainDrawWithSlots} disabled={generatingDraw}>
                          {generatingDraw ? 'Generating...' : (hasDraw ? '↺ Main Draw' : 'Gen. Main Draw')}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={handleGenerateQualDraw}
                          disabled={generatingQualDraw} style={{ color: '#92400e', borderColor: '#fde68a' }}>
                          {generatingQualDraw ? '...' : 'Gen. Qual. Draw'}
                        </button>
                      </>
                    ) : (
                      <button className={hasDraw ? 'btn btn-outline btn-sm' : 'btn btn-primary btn-sm'}
                        onClick={handleGenerateDraw} disabled={generatingDraw}>
                        {generatingDraw ? 'Generating...' : (hasDraw ? '↺ Regenerate Draw' : 'Generate Draw')}
                      </button>
                    )
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Add at least 2 entries to generate a draw.</span>
                  )}
                  {hasDraw && (() => {
                    const real = matches.filter(m => m.player1_id && m.player2_id && !(m.notes || '').includes('BYE'));
                    if (real.length === 0) return null;
                    return (
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: 4 }}>
                        {real.filter(m => m.status === 'completed').length}/{real.length} matches done
                      </span>
                    );
                  })()}
                  {/* Assign Qualifiers — show whenever Q slots are still pending */}
                  {isOrganizer && isMainDraw && hasDraw && (qualSlots === null || qualSlots?.slots?.some(s => !s.is_filled)) && !qualPreview && (
                    <button className="btn btn-primary btn-sm" onClick={handlePreviewQualAssign}
                      style={{ marginLeft: 'auto', background: '#0F766E', borderColor: '#0F766E' }}>
                      Assign Qualifiers
                    </button>
                  )}
                </div>
              )}

              {/* Qualifier assignment preview modal */}
              {qualPreview && (
                <div style={{ background: '#F0FDFA', border: '1px solid #c4b5fd', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#5b21b6', marginBottom: '0.75rem' }}>
                    Random Draw — Qualifier Assignment Preview
                  </div>
                  <table className="table" style={{ fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                    <thead><tr><th>Q Slot</th><th>Assigned To</th></tr></thead>
                    <tbody>
                      {qualPreview.map(a => (
                        <tr key={a.qualifier_slot}>
                          <td><strong style={{ color: '#0F766E' }}>{a.qualifier_slot}</strong></td>
                          <td>{a.winner_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setQualPreview(null)}>Re-draw</button>
                    <button className="btn btn-primary btn-sm" onClick={handleConfirmQualAssign}
                      disabled={assigningQual} style={{ background: '#0F766E', borderColor: '#0F766E' }}>
                      {assigningQual ? 'Assigning...' : 'Confirm & Assign'}
                    </button>
                  </div>
                </div>
              )}

              {/* Q-slot status summary + refresh */}
              {isMainDraw && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
                  {qualSlots?.slots?.length > 0
                    ? qualSlots.slots.map(s => (
                        <span key={s.qualifier_slot} style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600, background: s.is_filled ? '#d1fae5' : '#fef3c7', color: s.is_filled ? '#065f46' : '#92400e' }}>
                          {s.qualifier_slot}: {s.is_filled ? s.winner : 'Pending'}
                        </span>
                      ))
                    : qualSlots && <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>All Q slots filled</span>
                  }
                  <button onClick={loadQualSlots} disabled={loadingQualSlots} title="Refresh qualifier status"
                    style={{ fontSize: '0.72rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', color: '#6b7280' }}>
                    {loadingQualSlots ? '…' : '↺'}
                  </button>
                  {/* Assign Qualifiers — also shown here */}
                  {isOrganizer && hasDraw && (qualSlots === null || qualSlots?.slots?.some(s => !s.is_filled)) && !qualPreview && (
                    <button className="btn btn-primary btn-sm" onClick={handlePreviewQualAssign}
                      style={{ background: '#0F766E', borderColor: '#0F766E' }}>
                      Assign Qualifiers
                    </button>
                  )}
                </div>
              )}

              {/* PFQ: Withdraw a main draw player → promote best qualifying loser */}
              {isOrganizer && isMainDraw && hasDraw && (() => {
                const round1Players = matches
                  .filter(m => m.round_number === 1 && !(m.notes || '').includes('BYE') && m.status !== 'completed')
                  .flatMap(m => [
                    m.player1_id ? { id: m.player1_id, name: m.player1_display || m.player1_name } : null,
                    m.player2_id ? { id: m.player2_id, name: m.player2_display || m.player2_name } : null,
                  ].filter(Boolean));
                if (round1Players.length === 0) return null;
                const selected = round1Players.find(p => p.id === pfqSelected);
                return (
                  <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {!pfqOpen ? (
                      <button onClick={() => { setPfqOpen(true); setPfqSelected(''); }}
                        style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: 5, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', cursor: 'pointer', fontWeight: 600 }}>
                        Withdraw Player (PFQ)
                      </button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', padding: '0.5rem 0.75rem', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                        <select value={pfqSelected} onChange={e => setPfqSelected(e.target.value)}
                          style={{ fontSize: '0.78rem', padding: '3px 6px', borderRadius: 4, border: '1px solid #fbbf24', background: 'white', color: '#92400e', minWidth: 160 }}>
                          <option value=''>— Select player to withdraw —</option>
                          {round1Players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <button disabled={!pfqSelected || pfqLoading} onClick={() => handlePFQ(pfqSelected, selected?.name)}
                          style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: 5, background: pfqSelected ? '#c2410c' : '#e5e7eb', color: pfqSelected ? 'white' : '#9ca3af', border: 'none', cursor: pfqSelected ? 'pointer' : 'default', fontWeight: 600 }}>
                          {pfqLoading ? 'Withdrawing…' : 'Confirm Withdraw'}
                        </button>
                        <button onClick={() => { setPfqOpen(false); setPfqSelected(''); }}
                          style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 5, background: 'none', border: '1px solid #e5e7eb', color: '#6b7280', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* PFR: Fill vacant qualifying slots from reserve list */}
              {isOrganizer && isQualifyingChild && hasDraw && (() => {
                const vacant = matches
                  .filter(m => !(m.notes || '').includes('BYE') && m.status !== 'completed')
                  .flatMap(m => [
                    !m.player1_id ? { matchId: m.id, position: 'player1_id', label: `M${m.match_number} · Player 1` } : null,
                    !m.player2_id ? { matchId: m.id, position: 'player2_id', label: `M${m.match_number} · Player 2` } : null,
                  ].filter(Boolean));
                if (vacant.length === 0) return null;
                return (
                  <div style={{ marginBottom: '0.75rem', padding: '0.65rem 0.85rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.4rem' }}>
                      Fill from Reserve · PFR
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#075985', marginBottom: '0.5rem' }}>
                      Promotes the top-ranked reserve player into a vacant qualifying slot.
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {vacant.map(v => (
                        <button key={v.matchId + v.position} disabled={pfrLoading === v.matchId + v.position}
                          onClick={() => handlePFR(v.matchId, v.position)}
                          style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: 5, background: 'white', border: '1px solid #bae6fd', color: '#0369a1', cursor: 'pointer', opacity: pfrLoading === v.matchId + v.position ? 0.6 : 1 }}>
                          {pfrLoading === v.matchId + v.position ? 'Filling…' : `Fill ${v.label}`}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {loadingMatches ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loading draw...</div>
              ) : !hasDraw ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' }}>
                  No draw generated yet.{canDraw && isOrganizer ? ' Use "Generate Draw" above.' : ''}
                </div>
              ) : isLeague ? (
                /* ── League: standings + fixture list ── */
                <>
                  {standings.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Standings</div>
                      <table className="table" style={{ fontSize: '0.8rem' }}>
                        <thead><tr><th>#</th><th>Player</th><th>P</th><th>W</th><th>L</th><th>Pts</th><th>Sets W</th><th>Sets L</th></tr></thead>
                        <tbody>
                          {standings.map((s, i) => (
                            <tr key={s.player_id} style={{ background: i === 0 ? '#f0fff4' : 'inherit' }}>
                              <td>{i + 1}</td>
                              <td style={{ fontWeight: i === 0 ? 700 : 500 }}>{s.player_name}</td>
                              <td>{s.played}</td>
                              <td style={{ color: '#28a745' }}>{s.won}</td>
                              <td style={{ color: '#dc3545' }}>{s.lost}</td>
                              <td style={{ fontWeight: 700, color: '#0D9488' }}>{s.points}</td>
                              <td style={{ color: '#6c757d' }}>{s.sets_won}</td>
                              <td style={{ color: '#6c757d' }}>{s.sets_lost}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Fixtures</div>
                    {rounds.map(round => {
                      const roundMatches = matches.filter(m => m.round_number === round);
                      return (
                        <div key={round} style={{ marginBottom: '0.75rem' }}>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.3rem' }}>Round {round}</div>
                          {roundMatches.map(match => {
                            const isBye = (match.notes || '').includes('BYE');
                            if (isBye) return null;
                            const p1 = match.player1_display || match.player1_name || (!match.player1_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : 'TBD');
                            const p2 = match.player2_display || match.player2_name || (!match.player2_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : 'TBD');
                            const qualIds = new Set(qualSlots?.qualifyingPlayerIds || []);
                            const done = match.status === 'completed';
                            const scoring = scoreTarget === match.id;
                            return (
                              <div key={match.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.75rem', marginBottom: '0.25rem', background: done ? '#f8fff9' : '#fafafa', border: done ? '1px solid #c3e6cb' : '1px solid #e9ecef', borderRadius: 6, fontSize: '0.82rem' }}>
                                <span style={{ flex: 1, fontWeight: match.winner_id === match.player1_id ? 700 : 400, textAlign: 'right', color: match.winner_id && match.winner_id !== match.player1_id ? '#c4c4c4' : '#111827' }}>{p1}{match.player1_id && qualIds.has(match.player1_id) && <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: 3, fontWeight: 600 }}>[Q]</span>}</span>
                                <span style={{ color: '#d1d5db', fontSize: '0.7rem' }}>vs</span>
                                <span style={{ flex: 1, fontWeight: match.winner_id === match.player2_id ? 700 : 400, color: match.winner_id && match.winner_id !== match.player2_id ? '#c4c4c4' : '#111827' }}>{p2}{match.player2_id && qualIds.has(match.player2_id) && <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: 3, fontWeight: 600 }}>[Q]</span>}</span>
                                {done ? (
                                  <span style={{ fontSize: '0.68rem', background: '#d1fae5', color: '#065f46', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>✓ {match.winner_name || 'Done'}</span>
                                ) : (isOrganizer && match.player1_id && match.player2_id) ? (
                                  <button onClick={() => setScoreTarget(scoring ? null : match.id)}
                                    style={{ fontSize: '0.72rem', background: scoring ? '#F0FDFA' : '#f9fafb', color: '#0D9488', border: '1px solid #99F6E4', borderRadius: 5, padding: '2px 9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    {scoring ? 'Cancel' : 'Result'}
                                  </button>
                                ) : null}
                                {scoring && (
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginLeft: 8 }}>
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Winner:</span>
                                    <button className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => handleScoreWinner(match.id, match.player1_id)}>{p1}</button>
                                    <button className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => handleScoreWinner(match.id, match.player2_id)}>{p2}</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                /* ── Knockout: visual bracket ── */
                <>
                  <KnockoutBracket matches={matches} getRoundName={getRoundName} qualifyingPlayerIds={new Set(qualSlots?.qualifyingPlayerIds || [])} />
                  {/* Result entry below bracket */}
                  {isOrganizer && rounds.some(round => matches.some(m => m.round_number === round && m.player1_id && m.player2_id && !(m.notes || '').includes('BYE') && m.status !== 'completed')) && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Enter Results</div>
                      {rounds.map(round => {
                        const roundMatches = matches.filter(m => m.round_number === round && m.player1_id && m.player2_id && !(m.notes || '').includes('BYE') && m.status !== 'completed');
                        if (roundMatches.length === 0) return null;
                        return (
                          <div key={round} style={{ marginBottom: '0.5rem' }}>
                            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.3rem' }}>{getRoundName(round)}</div>
                            {roundMatches.map(match => {
                              const p1 = match.player1_display || match.player1_name || (!match.player1_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : 'TBD');
                              const p2 = match.player2_display || match.player2_name || (!match.player2_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : 'TBD');
                              const qualIds2 = new Set(qualSlots?.qualifyingPlayerIds || []);
                              const scoring = scoreTarget === match.id;
                              return (
                                <div key={match.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.4rem 0.75rem', marginBottom: '0.25rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.82rem' }}>
                                  <span style={{ flex: 1, textAlign: 'right' }}>{p1}{match.player1_id && qualIds2.has(match.player1_id) && <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: 3, fontWeight: 600 }}>[Q]</span>}</span>
                                  <span style={{ color: '#d1d5db', fontSize: '0.7rem' }}>vs</span>
                                  <span style={{ flex: 1 }}>{p2}{match.player2_id && qualIds2.has(match.player2_id) && <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: 3, fontWeight: 600 }}>[Q]</span>}</span>
                                  <button onClick={() => setScoreTarget(scoring ? null : match.id)}
                                    style={{ fontSize: '0.72rem', background: scoring ? '#F0FDFA' : '#f9fafb', color: '#0D9488', border: '1px solid #99F6E4', borderRadius: 5, padding: '2px 9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    {scoring ? 'Cancel' : 'Result'}
                                  </button>
                                  {scoring && (
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Winner:</span>
                                      <button className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => handleScoreWinner(match.id, match.player1_id)}>{p1}</button>
                                      <button className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => handleScoreWinner(match.id, match.player2_id)}>{p2}</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── EventsTab ────────────────────────────────────────────────────────────────

const EventsTab = ({ tournament, isOrganizer, onPlayersChange }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const res = await eventApi.list(tournament.id);
      setEvents(res.data);
    } catch {} finally { setLoading(false); }
  }, [tournament.id]);

  // Reload both the event list and the parent's player list after any entry change
  const handleChanged = useCallback(() => {
    loadEvents();
    onPlayersChange?.();
  }, [loadEvents, onPlayersChange]);

  // Called by EventCard when a single event is deleted — remove from local state immediately
  const handleEventDeleted = useCallback((deletedId) => {
    setEvents(prev => prev.filter(e => e.id !== deletedId));
    onPlayersChange?.();
  }, [onPlayersChange]);

  const handleDeleteAllEvents = async () => {
    if (!window.confirm(`Delete ALL ${events.length} events for this tournament? This will permanently remove all entries and draws for every event. This cannot be undone.`)) return;
    setDeletingAll(true);
    try {
      await Promise.all(events.map(ev => eventApi.delete(tournament.id, ev.id)));
      setEvents([]);
      onPlayersChange?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete all events');
      await loadEvents(); // resync on partial failure
    } finally { setDeletingAll(false); }
  };

  useEffect(() => { loadEvents(); }, [loadEvents]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <span style={{ fontWeight: 600, color: '#111827' }}>{events.length} event{events.length !== 1 ? 's' : ''}</span>
          <span style={{ color: '#9ca3af', fontSize: '0.8rem', marginLeft: 8 }}>
            {events.reduce((s, e) => s + (parseInt(e.player_count) || 0), 0)} total entries
          </span>
        </div>
        {isOrganizer && !showAddEvent && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {events.length > 0 && (
              <button
                className="btn btn-sm"
                style={{ border: '1px solid #fca5a5', color: '#dc2626', background: 'white' }}
                onClick={handleDeleteAllEvents}
                disabled={deletingAll}>
                {deletingAll ? 'Deleting…' : 'Delete All Events'}
              </button>
            )}
            <button className="btn btn-outline btn-sm" onClick={() => setShowImport(true)}>⬆ Import Entries</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddEvent(true)}>+ Add Event</button>
          </div>
        )}
      </div>

      {isOrganizer && showAddEvent && (
        <AddEventForm
          tournament={tournament}
          sportType={tournament.sport_type}
          onSaved={() => { loadEvents(); setShowAddEvent(false); }}
          onCancel={() => setShowAddEvent(false)}
        />
      )}

      {events.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon">🏆</span>
            <h3 style={{ fontWeight: 600, marginBottom: '0.4rem', color: '#374151' }}>No events yet</h3>
            <p style={{ fontSize: '0.875rem' }}>
              {isOrganizer ? 'Add events like "Men Singles Open", "Women Doubles U18", etc.' : 'No events have been created for this tournament yet.'}
            </p>
          </div>
        </div>
      ) : (
        events.map(event => (
          <EventCard key={event.id} event={event} tournament={tournament} isOrganizer={isOrganizer} onChanged={handleChanged} onDeleted={handleEventDeleted} allEvents={events} />
        ))
      )}

      {showImport && (
        <ImportEntriesModal
          tournamentId={tournament.id}
          onClose={() => setShowImport(false)}
          onImported={handleChanged}
        />
      )}
    </div>
  );
};

export default EventsTab;
